import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  AttachmentTypesResponse,
  AttendanceReportListResponse,
  BulkReportResponse,
  CustomFilterSettings,
  NewReportDataType,
  OverwriteReportDataType,
  QueryOption,
  ReportAccessData,
  ReportAccessResponse,
  ReportResponse,
  ReportScheduleEmailType,
  ReportTestEmailType,
  SavedReportDataType,
  SavedReportsListResponse,
} from '../@types';
import {
  createReport,
  createSavedReportAccess,
  createScheduleReport,
  deleteReportById,
  getAttachmentTypes,
  getAttendanceReports,
  getDefaultReportByReportType,
  getReportAccessByReportCode,
  getReportByCode,
  getReportScheduleDataByReportId,
  getSavedReportAccessByReportId,
  getSavedReportsByCreatedId,
  sendSavedReportTestEmail,
  updateBulkReports,
  updateReportAccessDataById,
  updateReportByCode,
  updateReportScheduleEmailByReprtId,
} from '../api/report';
import {
  AttendanceReportsKey,
  defaultReportsKey,
  GetAttachmentTypesKey,
  GetReportsKey,
  SavedReportsAccessKey,
  SavedReportsKey,
  ScheduleReportKey,
} from '../configs/reports';

export const useGetAllAttendanceReports = (
  queryOption: QueryOption,
  customFilter?: CustomFilterSettings,
  enabled: boolean = true
): UseQueryResult<AttendanceReportListResponse> => {
  return useQuery({
    // customFilter belongs in the key: `getAttendanceReports` puts the
    // location, the include-children flag and the date window in the URL, but
    // without them here changing any of the four produced an identical key and
    // the cached, differently-filtered page came straight back — which is why
    // the page carried a manual refetch effect.
    queryKey: [AttendanceReportsKey, queryOption, customFilter],
    queryFn: () => getAttendanceReports(queryOption, customFilter),
    enabled,
  });
};

export const useGetSavedReports = (
  queryOption: QueryOption
): UseQueryResult<SavedReportsListResponse> => {
  return useQuery({
    queryKey: [GetReportsKey, queryOption.filterModel],
    queryFn: () => getSavedReportsByCreatedId(queryOption),
    enabled: false,
  });
};

export const useAddNewReport = (): UseMutationResult<ReportResponse, Error, NewReportDataType> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: NewReportDataType) => {
      return await createReport(data);
    },

    onSuccess: async (response: ReportResponse) => {
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsKey, response.id],
      });
    },
  });
};

export const useGetReportByCode = (code: string): UseQueryResult<SavedReportDataType> => {
  return useQuery({
    queryKey: [SavedReportsKey, code],
    queryFn: () => getReportByCode(code),
    enabled: false,
  });
};

export const useUpdateReportByCode = (
  code: string
): UseMutationResult<ReportResponse, Error, OverwriteReportDataType> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: OverwriteReportDataType) => {
      return await updateReportByCode(code, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsKey, code],
      });
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsKey],
      });
    },
  });
};

export const useAddNewSavedReportAccess = (): UseMutationResult<
  ReportAccessData,
  Error,
  { reportId: number; data: ReportAccessData }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { reportId: number; data: ReportAccessData }) => {
      return await createSavedReportAccess(data.reportId, data.data);
    },

    onSuccess: async (response: ReportAccessData) => {
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsAccessKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsAccessKey, response.id],
      });
    },
  });
};

export const useUpdateReportAccessDataById = (): UseMutationResult<
  ReportAccessData,
  Error,
  { id: number | string; data: ReportAccessData }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: number | string; data: ReportAccessData }) => {
      return await updateReportAccessDataById(String(data.id), data.data);
    },

    onSuccess: async (response: ReportAccessData) => {
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsAccessKey, response.id],
      });
      await queryClient.invalidateQueries({
        queryKey: [SavedReportsAccessKey],
      });
    },
  });
};

export const useGetShareAccessDataByReportId = (
  reportId: string
): UseQueryResult<ReportAccessResponse> => {
  return useQuery({
    queryKey: [SavedReportsAccessKey, reportId],
    queryFn: () => getSavedReportAccessByReportId(reportId),
    enabled: false,
    gcTime: 0,
  });
};

export const useGetReportAccessByReportCode = (code: string): UseQueryResult<ReportAccessData> => {
  return useQuery({
    queryKey: [SavedReportsAccessKey, code],
    queryFn: () => getReportAccessByReportCode(code),
    enabled: false,
    gcTime: 0,
  });
};

export const useGetAttachmentTypes = (): UseQueryResult<AttachmentTypesResponse> => {
  return useQuery({
    queryKey: [GetAttachmentTypesKey],
    queryFn: () => getAttachmentTypes(),
  });
};

export const useAddScheduleReport = (): UseMutationResult<
  ReportScheduleEmailType,
  Error,
  ReportScheduleEmailType
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ReportScheduleEmailType) => {
      return await createScheduleReport(data);
    },

    onSuccess: async (response: ReportScheduleEmailType) => {
      await queryClient.invalidateQueries({
        queryKey: [ScheduleReportKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [ScheduleReportKey, response.id],
      });
    },
  });
};

export const useGetScheduleReportDataByReportId = (
  reportId: string
): UseQueryResult<ReportScheduleEmailType> => {
  return useQuery({
    queryKey: [ScheduleReportKey, reportId],
    queryFn: () => getReportScheduleDataByReportId(reportId),
    enabled: false,
  });
};

export const useUpdateReportScheduleEmailByReportId = (
  reportId: string
): UseMutationResult<ReportScheduleEmailType, Error, ReportScheduleEmailType> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ReportScheduleEmailType) => {
      return await updateReportScheduleEmailByReprtId(reportId, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ScheduleReportKey, reportId],
      });
      await queryClient.invalidateQueries({
        queryKey: [ScheduleReportKey],
      });
    },
  });
};

export const useGetDefaultReportByReportType = (
  reportTypeName: string
): UseQueryResult<SavedReportDataType[]> => {
  return useQuery({
    queryKey: [defaultReportsKey, reportTypeName],
    queryFn: () => getDefaultReportByReportType(reportTypeName),
    gcTime: 0,
    enabled: false,
  });
};

export const useUpdateBulkReports = (): UseMutationResult<
  { success: boolean; updated: number },
  Error,
  BulkReportResponse[]
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkReportResponse[]) => {
      const response = await updateBulkReports(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [defaultReportsKey],
      });
    },
  });
};

export const useDeleteReportAccessDataById = (): UseMutationResult<
  { id: number },
  Error,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportId: number) => {
      return await deleteReportById(reportId);
    },

    onSuccess: async (response: { id: number }) => {
      await queryClient.invalidateQueries({
        queryKey: [GetReportsKey, response.id],
      });
    },
  });
};

export const useSendSavedReportTestEmail = (): UseMutationResult<
  { success: boolean; message: string },
  Error,
  ReportTestEmailType
> => {
  return useMutation({
    mutationFn: async (data: ReportTestEmailType) => {
      return await sendSavedReportTestEmail(data);
    },
  });
};
